import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Linking,
  Alert,
} from 'react-native';
import ScreenWrapper from '../../Components/ScreenWrapper';
import Header from '../../Components/Header';
import { theme } from '../../constants/theme';
import { hp, wp } from '../../helpers/common';
import { Mail, Phone, MessageCircle } from '../../../assets/icons';

const Help = () => {
  const supportDetails = {
    email: 'help@dynamicdivas.in',
    phone: '+919520721566',
    whatsapp: '+919520721566',
  };

  const handleEmailPress = () => {
    Linking.openURL(`mailto:${supportDetails.email}?subject=Dynamic Divas Support Request`);
  };

  const handlePhonePress = () => {
    Linking.openURL(`tel:${supportDetails.phone}`);
  };

  const handleWhatsAppPress = () => {
    const phoneNumber = supportDetails.whatsapp.replace(/\D/g, '');
    const whatsappUrl = `whatsapp://send?phone=${phoneNumber}&text=Hello! I need help with Dynamic Divas.`;
    const whatsappWebUrl = `https://wa.me/${phoneNumber}?text=Hello! I need help with Dynamic Divas.`;
    
    // Try to open WhatsApp directly without checking canOpenURL first
    Linking.openURL(whatsappUrl)
      .then(() => {
        // Success - WhatsApp opened
      })
      .catch((error) => {
        // If WhatsApp app fails, try WhatsApp web as fallback
        console.log('WhatsApp app failed to open, trying web version:', error);
        Linking.openURL(whatsappWebUrl)
          .then(() => {
            // WhatsApp web opened successfully
          })
          .catch((webError) => {
            // Both app and web failed
            console.log('WhatsApp web also failed:', webError);
            Alert.alert(
              'WhatsApp Not Available',
              'Unable to open WhatsApp. Please try again or use another contact method.',
              [{ text: 'OK' }]
            );
          });
      });
  };

  interface SupportItemProps {
    icon: React.ComponentType<{ width: number; height: number; color: string }>;
    title: string;
    subtitle: string;
    onPress: () => void;
    color: string;
  }

  const SupportItem = ({ icon: Icon, title, subtitle, onPress, color }: SupportItemProps) => (
    <TouchableOpacity style={styles.supportItem} onPress={onPress}>
      <View style={[styles.iconContainer, { backgroundColor: color }]}>
        <Icon width={24} height={24} color="white" />
      </View>
      <View style={styles.supportText}>
        <Text style={styles.supportTitle}>{title}</Text>
        <Text style={styles.supportSubtitle}>{subtitle}</Text>
      </View>
    </TouchableOpacity>
  );

  return (
    <ScreenWrapper>
      <Header title="Help & Support" />
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.subtitle}>
            We're here to help! Contact us through any of the channels below.
          </Text>
        </View>

        <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
          <View style={styles.supportSection}>
            <Text style={styles.sectionTitle}>Contact Support</Text>
            
            <SupportItem
              icon={Mail}
              title="Email Support"
              subtitle={supportDetails.email}
              onPress={handleEmailPress}
              color={theme.colors.primary}
            />

            <SupportItem
              icon={Phone}
              title="Phone Support"
              subtitle={supportDetails.phone}
              onPress={handlePhonePress}
              color={theme.colors.accent2}
            />

            <SupportItem
              icon={MessageCircle}
              title="WhatsApp Support"
              subtitle={supportDetails.whatsapp}
              onPress={handleWhatsAppPress}
              color={theme.colors.accent2}
            />
          </View>

          <View style={styles.infoSection}>
            <Text style={styles.sectionTitle}>Support Hours</Text>
            <View style={styles.infoCard}>
              <Text style={styles.infoText}>
                <Text style={styles.bold}>Monday - Friday:</Text> 9:00 AM - 6:00 PM EST
              </Text>
              <Text style={styles.infoText}>
                <Text style={styles.bold}>Saturday:</Text> 10:00 AM - 4:00 PM EST
              </Text>
              <Text style={styles.infoText}>
                <Text style={styles.bold}>Sunday:</Text> Closed
              </Text>
            </View>
          </View>

          <View style={styles.infoSection}>
            <Text style={styles.sectionTitle}>Response Time</Text>
            <View style={styles.infoCard}>
              <Text style={styles.infoText}>
                <Text style={styles.bold}>Email:</Text> Within 24 hours
              </Text>
              <Text style={styles.infoText}>
                <Text style={styles.bold}>Phone:</Text> Immediate during business hours
              </Text>
              <Text style={styles.infoText}>
                <Text style={styles.bold}>WhatsApp:</Text> Within 2-4 hours
              </Text>
            </View>
          </View>

          <View style={styles.faqSection}>
            <Text style={styles.sectionTitle}>Frequently Asked Questions</Text>           
            <View style={styles.faqCard}>
              <Text style={styles.faqQuestion}>How do I join a community?</Text>
              <Text style={styles.faqAnswer}>
                Navigate to the Communities tab, browse available communities, and tap "Join" on the ones you're interested in.
              </Text>
            </View>
            <View style={styles.faqCard}>
              <Text style={styles.faqQuestion}>How do I report inappropriate content?</Text>
              <Text style={styles.faqAnswer}>
                Use the three-dot menu on any post and select "Report Spam" to flag inappropriate content for review.
              </Text>
            </View>
          </View>
        </ScrollView>
      </View>
    </ScreenWrapper>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  header: {
    paddingHorizontal: wp(4),
    paddingTop: hp(1),
    paddingBottom: hp(3),
    backgroundColor: theme.colors.backgroundLight,
  },
  subtitle: {
    fontSize: hp(2),
    color: theme.colors.textLight,
    lineHeight: hp(2.8),
  },
  content: {
    flex: 1,
    paddingHorizontal: wp(4),
  },
  supportSection: {
    marginTop: hp(2),
    marginBottom: hp(3),
  },
  sectionTitle: {
    fontSize: hp(2.5),
    fontWeight: '600',
    color: theme.colors.text,
    marginBottom: hp(2),
  },
  supportItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'white',
    padding: wp(4),
    borderRadius: theme.radius.md,
    marginBottom: hp(1.5),
    ...theme.shadows.small,
  },
  iconContainer: {
    width: wp(12),
    height: wp(12),
    borderRadius: theme.radius.md,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: wp(3),
  },
  supportText: {
    flex: 1,
  },
  supportTitle: {
    fontSize: hp(2.2),
    fontWeight: '600',
    color: theme.colors.text,
    marginBottom: hp(0.5),
  },
  supportSubtitle: {
    fontSize: hp(1.8),
    color: theme.colors.textLight,
  },
  infoSection: {
    marginBottom: hp(3),
  },
  infoCard: {
    backgroundColor: 'white',
    padding: wp(4),
    borderRadius: theme.radius.md,
    ...theme.shadows.small,
  },
  infoText: {
    fontSize: hp(1.8),
    color: theme.colors.text,
    marginBottom: hp(1),
    lineHeight: hp(2.4),
  },
  bold: {
    fontWeight: '600',
  },
  faqSection: {
    marginBottom: hp(4),
  },
  faqCard: {
    backgroundColor: 'white',
    padding: wp(4),
    borderRadius: theme.radius.md,
    marginBottom: hp(1.5),
    ...theme.shadows.small,
  },
  faqQuestion: {
    fontSize: hp(2),
    fontWeight: '600',
    color: theme.colors.text,
    marginBottom: hp(1),
  },
  faqAnswer: {
    fontSize: hp(1.7),
    color: theme.colors.textLight,
    lineHeight: hp(2.2),
  },
});

export default Help;
